// Portal queue — same tickets table as the unified queue but scoped to a
// single portal. Columns are configurable per portal; headers are sortable.
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { ArrowLeft, Plus, Lifebuoy, MagnifyingGlass, GearSix, DownloadSimple, Rows, ListDashes } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import CreateTicketDialog from '../../components/serviceDesk/CreateTicketDialog';
import ServiceDeskUpsell from '../../components/serviceDesk/ServiceDeskUpsell';
import QueueSummary from '../../components/serviceDesk/QueueSummary';
import QueueViewToggle from '../../components/serviceDesk/QueueViewToggle';
import QueueStats, { STAT_PREDICATES } from '../../components/serviceDesk/QueueStats';
import QueueTable from '../../components/serviceDesk/QueueTable';
import ExportTicketsDialog from '../../components/serviceDesk/ExportTicketsDialog';
import { Dot, STATUS_DOT, PRIORITY_DOT, STATUS_OPTIONS, PRIORITY_OPTIONS, statusLabel, priorityLabel, CATEGORY_OPTIONS, categoryLabel } from '../../components/serviceDesk/ticketMeta';
import { sortTickets, DEFAULT_QUEUE_COLUMNS } from '../../components/serviceDesk/queueColumns';

export default function PortalQueuePage() {
  const { portalId } = useParams();
  const { API, getOrgHeaders, currentOrg, user } = useAuth();
  const { caps } = useSubscription();
  const navigate = useNavigate();
  const [portal, setPortal] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState('created');
  const [sortDir, setSortDir] = useState('desc');
  const [showCreate, setShowCreate] = useState(false);
  const [view, setView] = useState('list');
  const [showExport, setShowExport] = useState(false);
  const [quickFilter, setQuickFilter] = useState(null);
  const [density, setDensity] = useState(() => localStorage.getItem('sd_queue_density') || 'comfortable');

  const sdEnabled = caps?.service_desk_enabled === true;
  const canManage = ['owner', 'admin', 'project_manager'].includes(currentOrg?.role);
  const canEdit = !!currentOrg?.role;
  const setDensityPref = (d) => { setDensity(d); localStorage.setItem('sd_queue_density', d); };

  const load = useCallback(async () => {
    if (!currentOrg || !sdEnabled) { setLoading(false); return; }
    setLoading(true);
    try {
      const [portalsRes, ticketsRes, membersRes] = await Promise.all([
        axios.get(`${API}/service-desk/portals`, { withCredentials: true, headers: getOrgHeaders() }),
        axios.get(`${API}/service-desk/tickets`, { params: { portal_id: portalId }, withCredentials: true, headers: getOrgHeaders() }),
        axios.get(`${API}/members`, { withCredentials: true, headers: getOrgHeaders() }).catch(() => ({ data: [] })),
      ]);
      setPortal((portalsRes.data || []).find(p => p.portal_id === portalId) || null);
      setTickets(ticketsRes.data || []);
      setMembers(membersRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [currentOrg, sdEnabled, portalId, API, getOrgHeaders]);

  useEffect(() => { load(); }, [load]);

  const memberById = useMemo(() => Object.fromEntries(members.map(m => [m.user_id, m])), [members]);
  const assigneeOptions = useMemo(() => {
    const ids = [...new Set((tickets || []).map(t => t.assignee_user_id).filter(Boolean))];
    return ids.map(id => ({ id, name: memberById[id]?.name || memberById[id]?.email || 'Unknown' }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [tickets, memberById]);
  const columns = (portal?.queue_columns?.length ? portal.queue_columns : DEFAULT_QUEUE_COLUMNS);

  const onSort = (col) => {
    if (sortBy === col) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('asc'); }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = tickets.filter(t => {
      if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      if (priorityFilter !== 'all' && t.priority !== priorityFilter) return false;
      if (categoryFilter !== 'all' && (t.category || 'service_request') !== categoryFilter) return false;
      if (assigneeFilter === 'me' && t.assignee_user_id !== user?.user_id) return false;
      if (assigneeFilter === 'unassigned' && t.assignee_user_id) return false;
      if (!['all', 'me', 'unassigned'].includes(assigneeFilter) && t.assignee_user_id !== assigneeFilter) return false;
      if (q && !(`${t.ticket_key} ${t.title}`.toLowerCase().includes(q))) return false;
      if (quickFilter && !STAT_PREDICATES[quickFilter]?.(t)) return false;
      return true;
    });
    return sortTickets(list, sortBy, sortDir, { memberById });
  }, [tickets, statusFilter, priorityFilter, categoryFilter, assigneeFilter, query, sortBy, sortDir, memberById, user, quickFilter]);

  const onQuickEdit = async (ticketId, action, value) => {
    const body = action === 'assignee' ? { assignee_user_id: value } : { [action]: value };
    try {
      const { data } = await axios.patch(`${API}/service-desk/tickets/${ticketId}`, body, { withCredentials: true, headers: getOrgHeaders() });
      setTickets(prev => prev.map(t => (t.ticket_id === ticketId ? { ...t, ...data } : t)));
      toast.success('Ticket updated');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };
  const onBulk = async (action, value, ids) => {
    try {
      const { data } = await axios.post(`${API}/service-desk/tickets/bulk`, { ticket_ids: ids, action, value }, { withCredentials: true, headers: getOrgHeaders() });
      toast.success(`Updated ${data.updated} ticket${data.updated === 1 ? '' : 's'}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk update failed'); }
  };

  if (!sdEnabled) return <ServiceDeskUpsell />;
  if (loading) return <div className="flex-1 flex items-center justify-center text-zinc-400">Loading…</div>;
  if (!portal) return <div className="flex-1 flex items-center justify-center text-zinc-400">Portal not found</div>;

  return (
    <div className="flex-1 overflow-auto bg-zinc-50" data-testid="sd-portal-queue-page">
      <div className="max-w-[1600px] mx-auto px-8 py-6">
        <button onClick={() => navigate('/service-desk')} className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 mb-4">
          <ArrowLeft size={14} /> Back to all queues
        </button>
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-md bg-blue-50 flex items-center justify-center text-blue-700 font-bold ring-1 ring-blue-100">
              {portal.key}
            </div>
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1">
                <Lifebuoy size={14} weight="fill" /> Portal
              </div>
              <h1 className="text-2xl font-bold text-zinc-900 font-['Outfit']">{portal.name}</h1>
              <p className="text-sm text-zinc-500">{portal.description || `${portal.ticket_counter || 0} tickets created`}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canManage && (
              <Button size="sm" variant="outline" onClick={() => setShowExport(true)} data-testid="sd-portal-export-btn">
                <DownloadSimple size={14} className="mr-1.5" /> Export
              </Button>
            )}
            {canManage && (
              <Button size="sm" variant="outline" onClick={() => navigate(`/service-desk/settings/portal/${portal.portal_id}`)} data-testid="sd-portal-manage-btn">
                <GearSix size={14} className="mr-1.5" /> Manage portal
              </Button>
            )}
            <Button size="sm" onClick={() => setShowCreate(true)} data-testid="sd-portal-new-ticket-btn">
              <Plus size={14} className="mr-1.5" /> New ticket
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4" data-testid="sd-portal-queue-view-toggle-row">
          <QueueViewToggle view={view} onChange={setView} />
          {view === 'summary' && (
            <span className="text-xs text-zinc-400">{tickets.length} {tickets.length === 1 ? 'ticket' : 'tickets'} in scope</span>
          )}
        </div>

        {view === 'list' && (
          <QueueStats tickets={tickets} active={quickFilter} onPick={setQuickFilter} />
        )}

        {view === 'list' && (
        <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="sd-portal-queue-toolbar">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by key or title…"
              className="w-full h-9 pl-9 pr-3 rounded-md border border-zinc-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              data-testid="sd-portal-search" />
          </div>
          <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
            <SelectTrigger className="h-9 w-[150px]" data-testid="sd-portal-assignee-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tickets</SelectItem>
              <SelectItem value="me">Assigned to me</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {assigneeOptions.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-[150px]" data-testid="sd-portal-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_OPTIONS.map(s => (
                <SelectItem key={s} value={s}><span className="flex items-center gap-2"><Dot className={STATUS_DOT[s]} />{statusLabel(s)}</span></SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="h-9 w-[145px]" data-testid="sd-portal-priority-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All priorities</SelectItem>
              {PRIORITY_OPTIONS.map(p => (
                <SelectItem key={p} value={p}><span className="flex items-center gap-2"><Dot className={PRIORITY_DOT[p]} />{priorityLabel(p)}</span></SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="h-9 w-[155px]" data-testid="sd-portal-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {CATEGORY_OPTIONS.map(c => <SelectItem key={c} value={c}>{categoryLabel(c)}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="ml-auto flex items-center gap-2">
            <div className="flex items-center rounded-md border border-zinc-200 bg-white p-0.5" data-testid="sd-portal-density-toggle">
              <button onClick={() => setDensityPref('comfortable')} title="Comfortable"
                className={`p-1.5 rounded ${density === 'comfortable' ? 'bg-zinc-900 text-white' : 'text-zinc-400 hover:text-zinc-700'}`} data-testid="sd-density-comfortable">
                <Rows size={14} />
              </button>
              <button onClick={() => setDensityPref('compact')} title="Compact"
                className={`p-1.5 rounded ${density === 'compact' ? 'bg-zinc-900 text-white' : 'text-zinc-400 hover:text-zinc-700'}`} data-testid="sd-density-compact">
                <ListDashes size={14} />
              </button>
            </div>
            <span className="text-xs text-zinc-400">{filtered.length} {filtered.length === 1 ? 'ticket' : 'tickets'}</span>
          </div>
        </div>
        )}

        {view === 'summary' ? (
          <QueueSummary tickets={tickets} memberById={memberById} />
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center">
            <p className="text-sm text-zinc-500">No tickets match your filters.</p>
          </div>
        ) : (
          <QueueTable
            tickets={filtered}
            columns={columns}
            ctx={{ memberById }}
            sortBy={sortBy} sortDir={sortDir} onSort={onSort}
            onRowClick={(t) => navigate(`/service-desk/portal/${portal.portal_id}/ticket/${t.ticket_id}`)}
            members={members} memberById={memberById}
            canEdit={canEdit} onQuickEdit={onQuickEdit} onBulk={onBulk}
            density={density} loading={loading}
          />
        )}

        <CreateTicketDialog
          open={showCreate}
          onClose={() => setShowCreate(false)}
          portals={portal ? [portal] : []}
          defaultPortalId={portal?.portal_id}
          onCreated={(ticket) => { setShowCreate(false); load(); navigate(`/service-desk/portal/${ticket.portal_id}/ticket/${ticket.ticket_id}`); }}
        />
        <ExportTicketsDialog
          open={showExport}
          onClose={() => setShowExport(false)}
          portalId={portal.portal_id}
          portalName={portal.name}
          filters={{ status: statusFilter, priority: priorityFilter, category: categoryFilter, assignee: assigneeFilter, q: query, userId: user?.user_id }}
        />
      </div>
    </div>
  );
}
